import React, { useEffect, useMemo, useState } from 'react';
import { HoverInsight } from '../types';
import MetricCard from '../components/MetricCard';
import api from '../lib/api';
import { DateRangePicker } from '../components/DateRangePicker';
import { inr, num, pctFmt, toISO, deltaMeta, Tile } from '../lib/metricFormat';
import { SortableTileGrid } from '../components/SortableTileGrid';
import { useTileOrder } from '../hooks/useTileOrder';
import { useGlobalDateRange } from '../context/GlobalDateRangeContext';
import { RotateCcw } from 'lucide-react';

interface OverviewProps {
  onHover: (insight: HoverInsight | null) => void;
}

interface OverviewData {
  period: { start_date: string; end_date: string };
  previous_period: { start_date: string; end_date: string };
  connections: { zoho: boolean; shopify: boolean; ads: boolean };
  tiles: {
    total_revenue: Tile; shopify_revenue: Tile; b2b_revenue: Tile;
    total_ad_spend: Tile; mer: Tile;
    adv: Tile; repeat_rate: Tile; total_orders: Tile;
  };
}

const DEFAULT_ORDER = [
  'total_revenue', 'shopify_revenue', 'b2b_revenue',
  'total_ad_spend', 'mer',
  'adv', 'repeat_rate', 'total_orders',
];

const cardBorder = '!border !border-black/70';

export default function Overview({ onHover }: OverviewProps) {
  const today = useMemo(() => new Date(), []);
  const ninetyDaysAgo = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 89); return d; }, []);
  const { globalRange, setGlobalRange } = useGlobalDateRange();
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [order, setOrder, resetOrder] = useTileOrder('overview-tile-order', DEFAULT_ORDER);

  useEffect(() => {
    setLoading(true);
    const params = { start_date: toISO(globalRange.from), end_date: toISO(globalRange.to) };
    api.get('/overview', { params })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [globalRange]);

  const t = data?.tiles;

  const renderTile = (id: string) => {
    switch (id) {
      case 'total_revenue':
        return <MetricCard className={cardBorder} platform="Blended" title="Total Revenue" value={t ? inr(t.total_revenue.value) : '—'} change={deltaMeta(t?.total_revenue).change} trend={deltaMeta(t?.total_revenue).trend} description="Shopify Net Sales + B2B Revenue from Zoho Books" onHover={onHover} />;
      case 'shopify_revenue':
        return <MetricCard className={cardBorder} platform="Shopify" title="Shopify Revenue (Net Sales)" value={t ? inr(t.shopify_revenue.value) : '—'} change={deltaMeta(t?.shopify_revenue).change} trend={deltaMeta(t?.shopify_revenue).trend} description="Net Sales, live from Shopify's own Analytics (ShopifyQL)" onHover={onHover} />;
      case 'b2b_revenue':
        return <MetricCard className={cardBorder} platform="Zoho Books" title="B2B Revenue" value={t ? inr(t.b2b_revenue.value) : '—'} change={deltaMeta(t?.b2b_revenue).change} trend={deltaMeta(t?.b2b_revenue).trend} description="Zoho invoices, excluding invoices billed to Shopify customers" onHover={onHover} />;
      case 'total_ad_spend':
        return <MetricCard className={cardBorder} platform="Meta + Google" title="Total Ad Spend" value={t ? inr(t.total_ad_spend.value) : '—'} change={deltaMeta(t?.total_ad_spend).change} trend={deltaMeta(t?.total_ad_spend).trend} description="Meta (NOOE + Palindrome) ad spend + Google Ads spend, combined" onHover={onHover} />;
      case 'mer':
        return <MetricCard className={cardBorder} platform="Calculated" title="MER (Marketing Efficiency Ratio)" value={t ? pctFmt(t.mer.value) : '—'} change={deltaMeta(t?.mer).change} trend={deltaMeta(t?.mer).trend} invertColor description="Total Ad Spend ÷ Total Revenue — lower is more efficient" onHover={onHover} />;
      case 'adv':
        return <MetricCard className={cardBorder} platform="Shopify" title="ADV (Avg. Order Value)" value={t ? inr(t.adv.value) : '—'} change={deltaMeta(t?.adv).change} trend={deltaMeta(t?.adv).trend} description="Shopify Gross Sales ÷ Total Orders in the selected period" onHover={onHover} />;
      case 'repeat_rate':
        return <MetricCard className={cardBorder} platform="Shopify" title="Repeat Rate (Orders)" value={t ? pctFmt(t.repeat_rate.value) : '—'} change={deltaMeta(t?.repeat_rate).change} trend={deltaMeta(t?.repeat_rate).trend} description="Shopify's own metric: returning customers ÷ all customers × 100" onHover={onHover} />;
      case 'total_orders':
        return <MetricCard className={cardBorder} platform="Shopify" title="Total Orders" value={t ? num(t.total_orders.value) : '—'} change={deltaMeta(t?.total_orders).change} trend={deltaMeta(t?.total_orders).trend} description="Total Shopify orders placed in the selected period" onHover={onHover} />;
      default:
        return null;
    }
  };

  if (loading && !data) {
    return (
      <div data-testid="overview-loading" className="flex items-center justify-center h-64 text-xs text-slate-500 font-mono uppercase tracking-widest">
        Loading live data…
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in -m-6 md:-m-8 p-6 md:p-8 min-h-full" style={{ background: 'linear-gradient(180deg, #F7EFE3 0%, #F3E8D8 100%)' }} data-testid="overview-page">
      {/* Header section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#e2d3ba] pb-6">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-600 bento-glow-dot"></span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#9b7d54] font-semibold">
              Live Operations
            </span>
          </div>
          <h1 className="text-3xl font-semibold font-arial text-slate-800 tracking-tight">
            Overview
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Defaults to Month-to-Date. Drag any tile to rearrange it — the date range you pick here applies across every module (except Scorecard), unless you set a different range there manually.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="overview-reset-layout-button"
            onClick={resetOrder}
            title="Reset tile layout"
            className="p-2.5 rounded-xl border bg-white text-slate-500 border-slate-200 hover:border-slate-400 hover:text-slate-800 transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
          <DateRangePicker value={globalRange} onChange={setGlobalRange} minDate={ninetyDaysAgo} maxDate={today} testIdPrefix="overview" />
        </div>
      </div>

      <SortableTileGrid
        order={order}
        onReorder={setOrder}
        renderTile={renderTile}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        testId="overview-tile-grid"
      />
    </div>
  );
}
